import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Typography } from 'antd';

import RangeDatePicker, {
  RangeDatePickerProps,
} from 'app/components/FormInput/RangeDatePicker';
import { ReactComponent as CalendarIcon } from 'app/assets/img/calendar-icon.svg';
import { ReactComponent as ClearIcon } from 'app/assets/img/close_icon.svg';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import {
  EN_DAY,
  VI_DAY,
} from 'app/pages/Cart/components/TicketItem/partials/components/CustomDatePicker';

interface RangeDatePickerCustomProps extends RangeDatePickerProps {
  placeholder?: string;
  startDateInit?: Date;
  endDateInit?: Date;
  showOnlyDate?: boolean;
  showSameTime?: boolean;
  syncFromProps?: boolean;
}

const RangeDatePickerCustom = (props: RangeDatePickerCustomProps) => {
  const {
    placeholder,
    onChange,
    isClearable,
    startDateInit,
    endDateInit,
    showOnlyDate,
    showSameTime,
    onCalendarClose,
    syncFromProps,
    ...restProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(
    startDateInit || null,
  );
  const [endDate, setEndDate] = useState<Date | null>(endDateInit || null);
  const [isMouseEnter, setIsMouseEnter] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { locale } = useGlobalFunction();
  useEffect(() => {
    if (!syncFromProps) return;
    if (isOpen) return;
    const nextStart = startDateInit ?? null;
    const nextEnd = endDateInit ?? null;
    const isSameStart =
      (!startDate && !nextStart) ||
      (startDate && nextStart && moment(startDate).isSame(nextStart));
    const isSameEnd =
      (!endDate && !nextEnd) ||
      (endDate && nextEnd && moment(endDate).isSame(nextEnd));
    if (!isSameStart) {
      setStartDate(nextStart);
    }
    if (!isSameEnd) {
      setEndDate(nextEnd);
    }
  }, [startDateInit, endDateInit, isOpen, syncFromProps]);

  const isEmptyTime = !startDate && !endDate;

  const isShowClearIcon = !!(isMouseEnter && (startDate || endDate));

  const renderText = () => {
    if (isEmptyTime) return placeholder ?? '';
    const startDateMoment = moment(startDate);
    const startDay = startDateMoment.day();

    let textDisplay = showOnlyDate
      ? ''
      : locale === 'vi'
      ? VI_DAY[startDay]
      : EN_DAY[startDay];
    textDisplay += ` (${startDateMoment.format('DD/MM/YYYY')})`;
    if (endDate) {
      const endDateMoment = moment(endDate);

      if (endDateMoment.isSame(startDateMoment) && !showSameTime)
        return textDisplay;

      const endDay = endDateMoment.day();
      textDisplay += ` - ${
        showOnlyDate ? '' : locale === 'vi' ? VI_DAY[endDay] : EN_DAY[endDay]
      } (${endDateMoment.format('DD/MM/YYYY')})`;
    }
    return textDisplay;
  };

  useEffect(() => {
    const fillDataIfEndDateEmpty = () => {
      if (isOpen) return;
      if (startDate && !endDate) setEndDate(startDate);
    };
    fillDataIfEndDateEmpty();
  }, [endDate, isOpen, startDate]);

  useEffect(() => {
    if (isClearable && isShowClearIcon) {
      const clearBtn = containerRef.current?.querySelector('.CLEAR_DATA');
      if (clearBtn) {
        clearBtn.addEventListener('mousemove', () => setIsMouseEnter(true));
        clearBtn.addEventListener('mouseleave', () => setIsMouseEnter(false));
      }
    }
  }, [isClearable, isShowClearIcon]);

  return (
    <Container className="range-picker-container" ref={containerRef}>
      <RangeDatePicker
        startDate={startDate}
        endDate={endDate}
        onChange={(value, event) => {
          const [start, end] = value;
          setStartDate(start);
          setEndDate(end);
          onChange(value, event);
        }}
        inline={false}
        customInput={
          <StyledInput
            className="date-picker"
            isFocus={isOpen}
            onMouseMove={() => isClearable && setIsMouseEnter(true)}
            onMouseLeave={() => isClearable && setIsMouseEnter(false)}
          >
            <Typography.Paragraph
              style={{
                color: isEmptyTime ? '#dcdcdc' : '#000000d9',
                fontStyle: isEmptyTime ? 'italic' : 'normal',
                marginBottom: 0,
                marginRight: 10,
              }}
              ellipsis={{ rows: 1, tooltip: isOpen ? false : renderText() }}
            >
              {renderText()}
            </Typography.Paragraph>
            {isShowClearIcon ? null : (
              <CalendarIcon
                style={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                }}
              />
            )}
          </StyledInput>
        }
        popperClassName="zIndex3"
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => {
          setIsOpen(false);
          onCalendarClose?.();
        }}
        styleSlots={{
          container: {
            maxHeight: 'initial',
            background: '#ffffff',
            boxShadow:
              '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
            padding: '24px 27px 24px',
            borderRadius: 20,
          },
        }}
        isClearable={isShowClearIcon}
        clearButtonClassName="CLEAR_DATA"
        responsive={false}
        {...restProps}
      />
    </Container>
  );
};

export default RangeDatePickerCustom;

const Container = styled.div`
  display: inline-block;
  .react-datepicker__close-icon {
    &::after {
      background: #c9c9c9;
      font-size: 16px;
      padding: 0;
    }
    &:hover {
      &::after {
        background: #a2a2a2;
      }
    }
  }
`;

const StyledInput = styled.div<{ isFocus: boolean }>`
  border: solid 1px #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  padding: 4px 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  transition: 0.25s;
  &:hover {
    border-color: #2997ff;
  }
  border-color: ${props => (props.isFocus ? '#2997ff' : '#d9d9d9')};
  box-shadow: ${props =>
    props.isFocus ? '0 0 0 2px rgba(0, 122, 255, 0.2)' : 'none'};
`;
